import React, { useState, useCallback, useEffect, useMemo } from "react";

import { usePaymentMethods, PaymentMethodRow } from "hooks/api";
import {
  CreditCardIcon,
  CircleCheckBigIcon,
  TrashIcon,
} from "lucide-react";

import { useTheme, Box, Stack } from "@mui/material";

import { Card } from "@repo/ui";
import {
  EmptyState,
  Loader,
  StatusChip,
  CreatePaymentMethodModal,
  DeletePaymentMethodModal,
  SetDefaultPaymentMethodModal,
  ActionDropdown,
} from "components";
import { PaymentMethodPreview } from "components/display/PaymentMethodPreview";
import { PAYMENT_METHOD_STATUSES } from "constants/statuses";

// Expiry is the ONE thing this surface shows that no other payment-method
// surface does — you come to Settings → Payment methods to see which card is
// about to lapse. Everything else about the row (brand glyph, "Visa •••• 4242")
// comes from the shared PaymentMethodPreview; this page used to hand-roll all of
// it, including a fourth copy of the brand→logo map (§36).
const expiryLabel = (method: PaymentMethodRow): string | undefined => {
  const month = String(method.expMonth ?? "").padStart(2, "0");
  const year = String(method.expYear ?? "");
  if (!month.trim() || !year.trim()) return undefined;
  return `Expires ${month}/${year}`;
};

interface PaymentMethodsProps {
  /** Controlled from BillingPage so the "Add payment method" action can live in the
   *  page Header (parity with Roles & Permissions) rather than on its own row here. */
  openCreateModal?: boolean;
  onCloseCreateModal?: () => void;
}

export const PaymentMethods = ({
  openCreateModal = false,
  onCloseCreateModal,
}: PaymentMethodsProps) => {
  const theme = useTheme();
  const { data, isLoading, refetch } = usePaymentMethods();

  // The raw saved methods, unadorned. The old mapping synthesized a `label` of
  // "VISA •••• 4242" — which the shared preview then has to detect and discard as
  // "not a real nickname", and which the delete/set-default modals rebuild
  // identically from brand+last4 when it is absent. Same strings, one fewer place
  // to keep them in step.
  const paymentMethods: PaymentMethodRow[] = useMemo(
    () => (data?.methods ?? []) as PaymentMethodRow[],
    [data?.methods]
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodRow | null>(null);
  const [openDeletePaymentMethodModal, setOpenDeletePaymentMethodModal] =
    useState(false);
  const [
    openSetDefaultPaymentMethodModal,
    setOpenSetDefaultPaymentMethodModal,
  ] = useState(false);

  useEffect(() => {
    if (!paymentMethods.length) {
      setSelectedPaymentMethod(null);
      return;
    }

    setSelectedPaymentMethod((prev) => {
      if (!prev) {
        return paymentMethods[0];
      }

      const updated = paymentMethods.find((method) => method.id === prev.id);
      return updated || paymentMethods[0];
    });
  }, [paymentMethods]);

  const handleSetAsDefaultPaymentMethod = useCallback(
    (row: PaymentMethodRow) => {
      setSelectedPaymentMethod(row);
      setOpenSetDefaultPaymentMethodModal(true);
    },
    []
  );

  const handleCloseSetDefaultPaymentMethodModal = () => {
    setOpenSetDefaultPaymentMethodModal(false);
  };

  const handleDeletePaymentMethod = useCallback((row: PaymentMethodRow) => {
    setSelectedPaymentMethod(row);
    setOpenDeletePaymentMethodModal(true);
  }, []);

  const handleCloseDeletePaymentMethodModal = () => {
    setOpenDeletePaymentMethodModal(false);
  };

  const getActionItems = useCallback(
    (row: PaymentMethodRow) => {
      return [
        {
          label: "Set as default",
          onClick: () => handleSetAsDefaultPaymentMethod(row),
          visible: !row.isDefault,
          icon: <CircleCheckBigIcon strokeWidth={1} />,
        },
        {
          label: "Delete",
          onClick: () => handleDeletePaymentMethod(row),
          divider: true,
          destructive: true,
          icon: <TrashIcon strokeWidth={1} />,
        },
      ];
    },
    [handleDeletePaymentMethod, handleSetAsDefaultPaymentMethod]
  );

  return (
    // NO `pt` here — the tab's leading is owned by its TabPanel in `BillingPage`.
    //
    // This carried `pt: 8` (32px), added when all three TabPanels were `p: 0` and this
    // tab genuinely had to supply its own offset. `BillingPage` later added `pt: 4` to
    // the two card tabs to solve the same problem from the other end, and the two fixes
    // STACKED: 16 (sticky `pb`) + 16 (panel `pt`) + 32 (here) = 64px, against Invoices'
    // 32. Neither comment was wrong when written; they were written a fix apart and
    // neither could see the other.
    //
    // Hence one owner. The panel sets the gap for all three tabs, and a tab's content
    // starts at its own top edge.
    <Stack spacing={4}>
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "55vh",
          }}
        >
          <Loader />
        </Box>
      ) : paymentMethods.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "55vh",
          }}
        >
          <EmptyState
            title="No payment methods added"
            text="Use the Add payment method action above to save a card."
            icon={<CreditCardIcon strokeWidth={1} size={32} />}
          />
        </Box>
      ) : (
        <Card sx={{ py: 2 }}>
          <Stack
            divider={
              <Box
                sx={{ borderBottom: `1px solid ${theme.palette.primary[100]}` }}
              />
            }
          >
            {paymentMethods.map((method) => (
              <Stack
                key={method.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={3}
                sx={{ py: 4 }}
              >
                <Stack
                  direction="row"
                  spacing={3}
                  alignItems="center"
                  minWidth={0}
                >
                  <PaymentMethodPreview
                    method={method}
                    caption={expiryLabel(method)}
                  />
                  {/* Chip sits beside the preview rather than inline with the
                      title, matching how PaymentCell places it. */}
                  {method.isDefault && (
                    <StatusChip
                      statuses={PAYMENT_METHOD_STATUSES}
                      status="default"
                      size="small"
                    />
                  )}
                </Stack>
                <Box sx={{ width: 44, flexShrink: 0 }}>
                  <ActionDropdown items={getActionItems(method)} />
                </Box>
              </Stack>
            ))}
          </Stack>
        </Card>
      )}

      <DeletePaymentMethodModal
        paymentMethod={selectedPaymentMethod}
        open={openDeletePaymentMethodModal}
        onClose={handleCloseDeletePaymentMethodModal}
        refetch={refetch}
      />
      <SetDefaultPaymentMethodModal
        paymentMethod={selectedPaymentMethod}
        open={openSetDefaultPaymentMethodModal}
        onClose={handleCloseSetDefaultPaymentMethodModal}
        refetch={refetch}
      />
      <CreatePaymentMethodModal
        open={openCreateModal}
        onClose={() => onCloseCreateModal?.()}
        refetch={refetch}
      />
    </Stack>
  );
};
